import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from './styles/theme';
import Layout from './components/common/Layout';
// import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import UserProfilePage from './pages/UserProfilePage';
import ImagesPage from './pages/ImagesPage';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import TagsPage from './pages/TagsPage';
import { AppProvider } from './contexts/AppContext';
import { LangProvider } from './contexts/langContext';
import { BrandingProvider } from './contexts/BrandingContext';
import Loading from './components/common/Loading';
import PptxFileDetailPage from './pages/PptxFileDetailPage';
import PptxFilesPage from './pages/PptxFilesPage';
import MaterialsPage from './pages/MaterialsPage';
import MaterialDetailPage from './pages/MaterialDetailPage';
import MaterialSearchPage from './pages/MaterialSearchPage';
import DocumentReader from './components/common/DocumentReader';
import { useBranding } from './contexts/BrandingContext';
import { useLang } from './contexts/langContext';
import OauthCodeExchangePage from './pages/OauthCodeExchangePage';
import UnsafeSignOn from './pages/UnsafeSignOn';
import YKOauthLandingPage from './pages/YKOauthLandingPage';
import AuditLogsPage from './pages/AuditLogsPage';
import ContentSlicingPage from './pages/ContentSlicingPage';
import E2ELoginPage from './pages/E2ELoginPage';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
};

const HomeRoute: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  return user ? <Navigate to="/projects" /> : <Navigate to="/login" />;
};

const App: React.FC = () => {
  const { productName } = useBranding();
  const { lang } = useLang();
  window.document.title = productName[lang as 'en' | 'zh-CN'] ?? productName.en;

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Layout>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/yk/landing" element={<YKOauthLandingPage />} />
            <Route path="/oauth/callback" element={<OauthCodeExchangePage />} />
            <Route path="/unsafe-sign-on" element={<UnsafeSignOn />} />
            <Route path="/e2e/login" element={<E2ELoginPage />} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/docs/:docType" element={<DocumentReader />} />
            <Route
              path="/tags"
              element={
                <PrivateRoute>
                  <TagsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <PrivateRoute>
                  <ProjectsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <PrivateRoute>
                  <ProjectDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/:id/materials"
              element={
                <PrivateRoute>
                  <MaterialsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/:id/materials/search"
              element={
                <PrivateRoute>
                  <MaterialSearchPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/:id/materials/:materialId"
              element={
                <PrivateRoute>
                  <MaterialDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <UserProfilePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/pptx-files"
              element={
                <PrivateRoute>
                  <PptxFilesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/pptx-files/:id"
              element={
                <PrivateRoute>
                  <PptxFileDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/materials"
              element={
                <PrivateRoute>
                  <MaterialsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/materials/search"
              element={
                <PrivateRoute>
                  <MaterialSearchPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/materials/:materialId"
              element={
                <PrivateRoute>
                  <MaterialDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/pictures"
              element={
                <PrivateRoute>
                  <ImagesPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <PrivateRoute>
                  <AuditLogsPage />
                </PrivateRoute>
              }
            />
          </Routes>
        </Layout>
      </Router>
    </ThemeProvider>
  );
};

const AppWithAuth: React.FC = () => (
  <AuthProvider>
    <AppProvider>
      <LangProvider>
        <BrandingProvider>
          <App />
        </BrandingProvider>
      </LangProvider>
    </AppProvider>
  </AuthProvider>
);

export default AppWithAuth;
